import {
  isFinishedRecord,
  isRunningRecord,
  isWorkTimeRecord,
  mealBreaksByWork,
  startOfDayMs,
  workTimeBetweenMs,
} from './workTimeToday';

/**
 * How "Oma päiväni" (ProjectOverviewMyDay) reads the signed-in user's own
 * records: what they are doing now, today's work, breaks and rides in order,
 * and their hours today, this week and this month.
 *
 * The hours are the dashboard's (workTimeToday.js): a work record's time from
 * its clock-in to its clock-out, or to now while it runs, less its meal
 * breaks; coffee breaks and rides stay in. The breaks are also summed on
 * their own, all of them. Break and ride records stand under their work
 * record (`parentRecordId`).
 */

const parseTime = (value) => (value ? Date.parse(value) : NaN);

const BREAK_KINDS = ['coffee', 'meal', 'break'];

/**
 * Whether a kind (myDayRecordKind) is a break.
 * @param {string|null} kind
 * @returns {boolean}
 */
export const isBreakKind = (kind) => BREAK_KINDS.includes(kind);

/**
 * What a record is in the day: 'work' (a work record of its own, as the
 * dashboard times it: isWorkTimeRecord), 'coffee' (BREAK_COFFEE), 'meal'
 * (BREAK_MEAL), 'break' (another BREAK) or 'ride' (RIDE); null for a record
 * the day leaves out — an absence, an item, a benefit, a deleted record, a
 * date-only stand-in, one with no clock-in, or a work record under another.
 * @param {Object} record
 * @returns {'work'|'coffee'|'meal'|'break'|'ride'|null}
 */
export const myDayRecordKind = (record) => {
  if (!record || record.deletedAt || record.isDateOnlyRecord) return null;
  if (!Number.isFinite(parseTime(record.clockInAt))) return null;
  const type = record.type || '';
  if (type === 'RIDE') return 'ride';
  if (type === 'BREAK_COFFEE') return 'coffee';
  if (type === 'BREAK_MEAL') return 'meal';
  if (type.includes('BREAK')) return 'break';
  return isWorkTimeRecord(record) ? 'work' : null;
};

/**
 * The start of the local day after that of `now`, in epoch ms.
 * @param {number} now epoch ms
 * @returns {number}
 */
export const startOfNextDayMs = (now) => {
  const day = new Date(startOfDayMs(now));
  day.setDate(day.getDate() + 1);
  return day.getTime();
};

/**
 * The start of the week of `now`: its Monday's midnight, local time.
 * @param {number} now epoch ms
 * @returns {number}
 */
export const startOfWeekMs = (now) => {
  const day = new Date(startOfDayMs(now));
  // getDay() counts from Sunday; the week starts on Monday.
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day.getTime();
};

/**
 * The start of the month of `now`: its first day's midnight, local time.
 * @param {number} now epoch ms
 * @returns {number}
 */
export const startOfMonthMs = (now) => {
  const day = new Date(startOfDayMs(now));
  day.setDate(1);
  return day.getTime();
};

/**
 * Since when the section needs the user's records: the start of this week or
 * of this month, whichever is earlier (a week can begin in the month before).
 * @param {number} now epoch ms
 * @returns {number}
 */
export const myDayPeriodStartMs = (now) =>
  Math.min(startOfWeekMs(now), startOfMonthMs(now));

/**
 * A record's time from `from` up to `now`, in ms: from its clock-in (or
 * `from`, when it began earlier) to its clock-out, or to `now` while it runs.
 * @param {Object} record
 * @param {number} from epoch ms
 * @param {number} now epoch ms
 * @returns {number}
 */
export const recordTimeBetweenMs = (record, from, now) => {
  const start = Math.max(parseTime(record.clockInAt), from);
  const end = isRunningRecord(record)
    ? now
    : Math.min(parseTime(record.clockOutAt), now);
  return end > start ? end - start : 0;
};

/**
 * Of two copies of the same record — the page's own (`stored`, which the
 * clock buttons keep up to date) and one loaded for the section (`loaded`) —
 * the one to show: the one saved later (`updatedAt`, which the API sets on
 * every write), else the page's. (A deleted record leaves both: the API moves
 * it to another table, and deleteRecordThunk takes it out of each copy.)
 * @param {Object} [stored]
 * @param {Object} [loaded]
 * @returns {Object}
 */
export const preferredRecordVersion = (stored, loaded) => {
  if (!stored) return loaded;
  if (!loaded) return stored;
  return parseTime(loaded.updatedAt) > parseTime(stored.updatedAt)
    ? loaded
    : stored;
};

/**
 * The user's hours from their records: their work today, this week and this
 * month, each up to `now` and less its meal breaks within the period
 * (workTimeBetweenMs), and their breaks today, every kind of them.
 * @param {Object[]} records the user's own records of the section's scope
 * @param {number} now epoch ms
 * @returns {{todayMs: number, breaksTodayMs: number, weekMs: number, monthMs: number}}
 */
export const myDayTotals = (records, now) => {
  const dayStart = startOfDayMs(now);
  const weekStart = startOfWeekMs(now);
  const monthStart = startOfMonthMs(now);
  const mealBreaks = mealBreaksByWork(records);
  return records.reduce(
    (totals, record) => {
      const kind = myDayRecordKind(record);
      if (kind === 'work') {
        const meals = mealBreaks.get(record.id) || [];
        totals.todayMs += workTimeBetweenMs(record, meals, dayStart, now);
        totals.weekMs += workTimeBetweenMs(record, meals, weekStart, now);
        totals.monthMs += workTimeBetweenMs(record, meals, monthStart, now);
      } else if (isBreakKind(kind)) {
        totals.breaksTodayMs += recordTimeBetweenMs(record, dayStart, now);
      }
      return totals;
    },
    { todayMs: 0, breaksTodayMs: 0, weekMs: 0, monthMs: 0 }
  );
};

/**
 * Whether a record falls on the day of `now`: it runs, or it began before the
 * day ended and ended after the day began (one with no clock-out time, if it
 * began on the day).
 * @param {Object} record
 * @param {number} now epoch ms
 * @returns {boolean}
 */
export const isRecordOfDay = (record, now) => {
  const start = parseTime(record.clockInAt);
  if (!(start < startOfNextDayMs(now))) return false;
  if (isRunningRecord(record)) return true;
  const end = parseTime(record.clockOutAt);
  const dayStart = startOfDayMs(now);
  return Number.isFinite(end) ? end > dayStart : start >= dayStart;
};

const byClockIn = (a, b) => parseTime(a.clockInAt) - parseTime(b.clockInAt);

/**
 * The day's records as the section lists them: each work record in clock-in
 * order with its breaks and rides under it (`nested`), in theirs; a break or
 * a ride whose work record is not among them stands on its own, in order.
 * @param {Object[]} records the user's own records of the section's scope
 * @param {number} now epoch ms (only its day counts)
 * @returns {{record: Object, kind: string, nested: boolean, running: boolean}[]}
 */
export const myDayTimeline = (records, now) => {
  const ofDay = records.filter(
    (record) => myDayRecordKind(record) && isRecordOfDay(record, now)
  );
  const workIds = new Set(
    ofDay
      .filter((record) => myDayRecordKind(record) === 'work')
      .map((record) => record.id)
  );
  const isUnderWork = (record) =>
    myDayRecordKind(record) !== 'work' && workIds.has(record.parentRecordId);
  const row = (record, nested) => ({
    record,
    kind: myDayRecordKind(record),
    nested,
    running: isRunningRecord(record),
  });

  return ofDay
    .filter((record) => !isUnderWork(record))
    .sort(byClockIn)
    .flatMap((record) => [
      row(record, false),
      ...ofDay
        .filter(
          (child) => isUnderWork(child) && child.parentRecordId === record.id
        )
        .sort(byClockIn)
        .map((child) => row(child, true)),
    ]);
};

const latestByClockIn = (records) =>
  records.reduce(
    (latest, record) =>
      !latest || byClockIn(record, latest) > 0 ? record : latest,
    null
  );

/**
 * What the user is doing now, by their records:
 * - 'coffee', 'meal' or 'break': on a break since `since` (a running break
 *   comes first, as the clock buttons allow nothing else during one);
 * - 'ride': on a ride since `since`;
 * - 'working': clocked in since `since`;
 * - 'clockedOut': nothing runs, but work was clocked out today: the day's
 *   work from `from` (its first clock-in) to `until` (its last clock-out);
 * - 'notClockedIn': no work today.
 * `work` is the work record the state belongs to (a break's or a ride's own,
 * the last one clocked out), for its assignment and project.
 * @param {Object[]} records the user's own records of the section's scope
 * @param {number} now epoch ms (only its day counts)
 * @returns {{kind: string, since?: number, from?: number, until?: number, work?: Object}}
 */
export const myDayStatus = (records, now) => {
  const running = records.filter(
    (record) => myDayRecordKind(record) && isRunningRecord(record)
  );
  const runningWork = latestByClockIn(
    running.filter((record) => myDayRecordKind(record) === 'work')
  );
  const workOf = (record) =>
    records.find((other) => other.id === record.parentRecordId) ||
    runningWork ||
    null;

  const pause = latestByClockIn(
    running.filter((record) => isBreakKind(myDayRecordKind(record)))
  );
  if (pause) {
    return {
      kind: myDayRecordKind(pause),
      since: parseTime(pause.clockInAt),
      work: workOf(pause),
    };
  }
  const ride = latestByClockIn(
    running.filter((record) => myDayRecordKind(record) === 'ride')
  );
  if (ride) {
    return {
      kind: 'ride',
      since: parseTime(ride.clockInAt),
      work: workOf(ride),
    };
  }
  if (runningWork) {
    return {
      kind: 'working',
      since: parseTime(runningWork.clockInAt),
      work: runningWork,
    };
  }

  const done = records.filter(
    (record) =>
      myDayRecordKind(record) === 'work' &&
      isFinishedRecord(record) &&
      isRecordOfDay(record, now)
  );
  if (done.length === 0) return { kind: 'notClockedIn' };
  const lastOut = done.reduce((latest, record) =>
    parseTime(record.clockOutAt) > parseTime(latest.clockOutAt)
      ? record
      : latest
  );
  return {
    kind: 'clockedOut',
    from: Math.min(...done.map((record) => parseTime(record.clockInAt))),
    until: parseTime(lastOut.clockOutAt),
    work: lastOut,
  };
};
